import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChart3, Calendar } from 'lucide-react';
import StorefrontTrafficAnalyticsSection from './StorefrontTrafficAnalyticsSection';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { useCurrency } from '../../../../../context/CurrencyContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type OverviewResponse = {
  range: { from: string; to: string };
  kpis: {
    ordersTotal: number;
    paidOrdersTotal: number;
    paidRevenueTotalUsd: number;
    newCustomersTotal: number;
    purchasesTotal: number;
    purchaseCostTotalUsd: number;
    lossesTotal: number;
    lossCostTotalUsd: number;
    preorderAwaitingReleaseTotal?: number;
    preorderBalanceDueTotal?: number;
    preorderOpenTotal?: number;
    preorderDepositLiabilityUsd?: number;
  };
  series: {
    ordersByDay: Array<{ day: string; value: number }>;
    revenueByDay: Array<{ day: string; value: number }>;
    newCustomersByDay: Array<{ day: string; value: number }>;
    purchasesByDay: Array<{ day: string; value: number }>;
    lossesByDay: Array<{ day: string; value: number }>;
  };
};

function isoDateInputValue(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function mergeSeries(
  baseDays: Array<{ day: string }>,
  a: Array<{ day: string; value: number }>,
  b: Array<{ day: string; value: number }>,
  aKey: string,
  bKey: string,
) {
  const aMap = new Map(a.map((p) => [p.day, p.value]));
  const bMap = new Map(b.map((p) => [p.day, p.value]));
  return baseDays.map((d) => ({
    day: d.day,
    [aKey]: aMap.get(d.day) ?? 0,
    [bKey]: bMap.get(d.day) ?? 0,
  }));
}

export default function AnalyticsDashboardPage() {
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const { formatMajorInCurrency } = useCurrency();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      // Analytics dashboard is intentionally global (not department-scoped).
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );

  const [from, setFrom] = React.useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return isoDateInputValue(d);
  });
  const [to, setTo] = React.useState(() => isoDateInputValue(new Date()));

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<OverviewResponse | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (from) qs.set('from', new Date(from).toISOString());
      if (to) qs.set('to', new Date(`${to}T23:59:59`).toISOString());
      const res = await globalAuthorizedFetch(`${API_BASE}/stats/overview?${qs.toString()}`);
      const body = (await res.json().catch(() => null)) as any;
      if (!res.ok) {
        throw new Error(body?.message || 'Unable to load analytics.');
      }
      if (body?.ok === false) {
        throw new Error(body?.message || 'Forbidden');
      }
      setData(body as OverviewResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to load analytics.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, globalAuthorizedFetch, to]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const k = data?.kpis ?? null;
  const series = data?.series ?? null;
  const baseDays = (series?.ordersByDay ?? []).map((p) => ({ day: p.day }));
  const ordersVsCustomers = series
    ? mergeSeries(baseDays, series.ordersByDay, series.newCustomersByDay, 'orders', 'newCustomers')
    : [];
  const purchasesVsLosses = series
    ? mergeSeries(baseDays, series.purchasesByDay, series.lossesByDay, 'purchasesUsd', 'lossesUsd')
    : [];

  const Card = ({
    title,
    value,
    note,
    tone = 'neutral',
  }: {
    title: string;
    value: string;
    note?: string;
    tone?: 'neutral' | 'good' | 'warn' | 'bad';
  }) => (
    <div className="workspace-stat-card">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</div>
      <div
        className={[
          'mt-1 text-2xl font-semibold',
          tone === 'good'
            ? 'text-emerald-700'
            : tone === 'warn'
              ? 'text-amber-700'
              : tone === 'bad'
                ? 'text-rose-700'
                : 'text-slate-900',
        ].join(' ')}
      >
        {value}
      </div>
      {note ? <div className="mt-1 text-xs text-slate-600">{note}</div> : null}
    </div>
  );

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath="/workspace/reports/analytics">
      <main className="workspace-page">
        <section>
          <div className="mb-2 flex items-center gap-2 text-brand-700">
            <BarChart3 className="h-6 w-6" strokeWidth={2} />
            <h1 className="workspace-page-title">
              {t('backoffice.analytics_title', 'Analytics dashboard')}
            </h1>
          </div>
          <p className="max-w-4xl workspace-page-subtitle text-slate-600">
            {t(
              'backoffice.analytics_subtitle',
              'A high-signal operational and financial view of orders, customers, purchases and losses.',
            )}
          </p>
        </section>

        <section className="workspace-panel">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <Calendar className="h-4 w-4" />
              {t('backoffice.analytics_range', 'Date range')}
            </div>
            <div className="grid w-full grid-cols-1 gap-3 sm:max-w-2xl sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('backoffice.analytics_from', 'From')}
                </label>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none ring-brand-500/30 focus:border-brand-500 focus:ring-2"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('backoffice.analytics_to', 'To')}
                </label>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none ring-brand-500/30 focus:border-brand-500 focus:ring-2"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => void load()}
                  disabled={loading}
                  className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
                >
                  {loading ? t('common.loading') : t('backoffice.analytics_refresh', 'Refresh')}
                </button>
              </div>
            </div>
          </div>

          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
          ) : null}

          {loading && !data ? (
            <p className="text-sm text-slate-600">{t('common.loading')}</p>
          ) : !k || !series ? (
            <p className="text-sm text-slate-600">{t('backoffice.analytics_empty', 'No analytics data yet.')}</p>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Card
                  title={t('analytics_paid_revenue_usd', { defaultValue: 'Paid revenue (USD)' })}
                  value={formatMajorInCurrency(k.paidRevenueTotalUsd, 'USD')}
                  tone="good"
                  note={`${k.paidOrdersTotal} paid orders`}
                />
                <Card
                  title={t('analytics_orders_created', { defaultValue: 'Orders created' })}
                  value={String(k.ordersTotal)}
                  note={t('analytics_all_statuses_in_range', { defaultValue: 'All statuses in range' })}
                />
                <Card
                  title={t('analytics_preorder_open', { defaultValue: 'Open pre-orders' })}
                  value={String(k.preorderOpenTotal ?? 0)}
                  note={t('analytics_preorder_states', {
                    defaultValue: 'Awaiting release: {{awaiting}} · Balance due: {{due}}',
                    awaiting: String(k.preorderAwaitingReleaseTotal ?? 0),
                    due: String(k.preorderBalanceDueTotal ?? 0),
                  })}
                  tone={(k.preorderOpenTotal ?? 0) > 0 ? 'warn' : 'neutral'}
                />
                <Card
                  title={t('analytics_preorder_liability_usd', { defaultValue: 'Pre-order liability (USD)' })}
                  value={formatMajorInCurrency(k.preorderDepositLiabilityUsd ?? 0, 'USD')}
                  tone={(k.preorderDepositLiabilityUsd ?? 0) > 0 ? 'warn' : 'good'}
                  note={t('analytics_preorder_liability_note', {
                    defaultValue: 'Deposits held as liability until fulfillment.',
                  })}
                />
                <Card
                  title={t('analytics_new_customers', { defaultValue: 'New customers' })}
                  value={String(k.newCustomersTotal)}
                  tone={k.newCustomersTotal > 0 ? 'good' : 'neutral'}
                />
                <Card
                  title={t('analytics_loss_exposure_usd', { defaultValue: 'Loss exposure (USD)' })}
                  value={formatMajorInCurrency(k.lossCostTotalUsd, 'USD')}
                  tone={k.lossCostTotalUsd > 0 ? 'warn' : 'good'}
                  note={`${k.lossesTotal} loss entries`}
                />
              </div>

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <div className="workspace-stat-card">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">
                      {t('analytics_orders_vs_new_customers', {
                        defaultValue: 'Orders vs new customers',
                      })}
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {t('common.daily', { defaultValue: 'Daily' })}
                    </div>
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={ordersVsCustomers} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="ordersFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.05} />
                          </linearGradient>
                          <linearGradient id="customersFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.30} />
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="orders" stroke="#0284c7" fill="url(#ordersFill)" strokeWidth={2} />
                        <Area type="monotone" dataKey="newCustomers" stroke="#16a34a" fill="url(#customersFill)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="workspace-stat-card">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">
                      {t('analytics_paid_revenue_usd', { defaultValue: 'Paid revenue (USD)' })}
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {t('common.daily', { defaultValue: 'Daily' })}
                    </div>
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={series.revenueByDay} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0.05} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Area
                          type="monotone"
                          dataKey="value"
                          name={t('common.revenue', { defaultValue: 'Revenue' })}
                          stroke="#4f46e5"
                          fill="url(#revFill)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <div className="workspace-stat-card">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">
                      {t('analytics_purchases_vs_losses_usd', {
                        defaultValue: 'Purchases vs losses (USD)',
                      })}
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {t('common.daily', { defaultValue: 'Daily' })}
                    </div>
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={purchasesVsLosses} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        <Bar
                          dataKey="purchasesUsd"
                          name={t('common.purchases', { defaultValue: 'Purchases' })}
                          fill="#0ea5e9"
                          radius={[6, 6, 0, 0]}
                        />
                        <Bar
                          dataKey="lossesUsd"
                          name={t('common.losses', { defaultValue: 'Losses' })}
                          fill="#f59e0b"
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="workspace-stat-card">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">
                      {t('analytics_operational_kpis', { defaultValue: 'Operational KPIs' })}
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Range totals</div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Card
                      title="Purchases recorded"
                      value={String(k.purchasesTotal)}
                      note={`${formatMajorInCurrency(k.purchaseCostTotalUsd, 'USD')} total cost`}
                    />
                    <Card
                      title="Loss entries"
                      value={String(k.lossesTotal)}
                      note={`${formatMajorInCurrency(k.lossCostTotalUsd, 'USD')} estimated`}
                      tone={k.lossCostTotalUsd > 0 ? 'warn' : 'good'}
                    />
                    <Card title="Paid order ratio" value={`${k.ordersTotal > 0 ? Math.round((k.paidOrdersTotal / k.ordersTotal) * 100) : 0}%`} note="Paid / total created" tone={k.paidOrdersTotal > 0 ? 'good' : 'neutral'} />
                    <Card title="Customer acquisition" value={`${k.ordersTotal > 0 ? (k.newCustomersTotal / Math.max(1, k.ordersTotal)).toFixed(2) : '0.00'}`} note="New customers per order" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        <StorefrontTrafficAnalyticsSection from={from} to={to} fetcher={globalAuthorizedFetch} />
      </main>
    </MainLayout>
  );
}

